import React, { useEffect, useState } from "react";
import SearchableSelect from "../components/SearchableSelect";
import axios from "axios";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import CategorySelect from "../components/CategorySelect";
import { useModal } from "../context/ModalContext";
import {
  Smartphone,
  Laptop,
  Tablet,
  Monitor,
  Gamepad2,
  Headphones,
  Camera,
  Watch,
  Cable,
  Wifi,
  Cpu,
  Printer,
} from "lucide-react";

const apiUrl = process.env.REACT_APP_API_URL;

export default function PostItemPage() {
  const { showAlert } = useModal();
  const uStr = localStorage.getItem("user") || sessionStorage.getItem("user");
  const storedUser = uStr ? JSON.parse(uStr) : null;

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [fields, setFields] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    title: "",
    price: "",
    phone: storedUser?.phone || "",
    category: "",
    description: "",
    location: "",
    extra: {},
  });

  const MAX_IMAGES = 6;
  const MAX_IMAGE_SIZE_MB = 5;

  /* ================= FETCH BASE DATA ================= */

  useEffect(() => {
    const fetchCategories = async () => {
      const res = await axios.get(`${apiUrl}/api/categories`);

      const iconMap = {
        smartphones: <Smartphone size={18} />,
        laptops: <Laptop size={18} />,
        tablets: <Tablet size={18} />,
        "tvs & monitors": <Monitor size={18} />,
        gaming: <Gamepad2 size={18} />,
        audio: <Headphones size={18} />,
        cameras: <Camera size={18} />,
        wearables: <Watch size={18} />,
        accessories: <Cable size={18} />,
        networking: <Wifi size={18} />,
        components: <Cpu size={18} />,
        printers: <Printer size={18} />,
      };

      const categoriesWithIcons = res.data.map((c) => ({
        ...c,
        icon: iconMap[c.name.toLowerCase()] || "📦",
      }));

      setCategories(categoriesWithIcons);
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!form.category) return;

    axios
      .get(`${apiUrl}/api/brands/${form.category}`)
      .then((res) => {
        setBrands(res.data || []);
      })
      .catch(() => setBrands([]));

    axios
      .get(`${apiUrl}/api/categories/${form.category}/fields`)
      .then((res) => {
        setFields(res.data || []);
      })
      .catch(() => setFields([]));
  }, [form.category]);

  useEffect(() => {
    if (!form.extra.Brand) return;

    axios
      .get(`${apiUrl}/api/models/${form.extra.Brand}`)
      .then((res) => setModels(res.data));
  }, [form.extra.Brand]);

  /* ================= HELPERS ================= */

  const brandName =
    brands.find(
      (b) =>
        b.id == form.extra.Brand ||
        b.name?.toLowerCase() === String(form.extra.Brand || "").toLowerCase()
    )?.name ||
    (typeof form.extra.Brand === "string" ? form.extra.Brand : "") ||
    "";
  const selectedCatName = categories.find((c) => c.id == form.category)?.name?.toLowerCase() || "";

  const shouldShowField = (field) => {
    if (!brandName || !field.rules) return true;

    const brand = brandName.toLowerCase();

    if (
      field.rules.hideForBrand?.length &&
      field.rules.hideForBrand
        .map((b) => (typeof b === "string" ? b : b.brand_name ?? "").toLowerCase())
        .includes(brand)
    ) {
      return false;
    }

    if (
      field.rules.onlyForBrand?.length &&
      !field.rules.onlyForBrand
        .map((b) => (typeof b === "string" ? b : b.brand_name ?? "").toLowerCase())
        .includes(brand)
    ) {
      return false;
    }

    return true;
  };

  const handleExtraChange = (key, value) => {
    setForm((s) => ({ ...s, extra: { ...s.extra, [key]: value } }));
  };

  /* ================= IMAGE HANDLING ================= */

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    setImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      const valid = files.slice(0, remaining).filter((f) => {
        if (f.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
          showAlert(`${f.name} too large`, "error");
          return false;
        }
        return true;
      });

      return [
        ...prev,
        ...valid.map((file) => ({
          file,
          preview: URL.createObjectURL(file),
        })),
      ];
    });

    e.target.value = "";
  };

  const setAsCover = (index) => {
    setImages((prev) => {
      const imgs = [...prev]; // copy array
      const [selected] = imgs.splice(index, 1); // remove selected
      imgs.unshift(selected); // move to front
      return imgs; // return array
    });
  };

  const removeImage = (index) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const isSubmitting = React.useRef(false);

  const handlePostItem = async () => {
    if (loading || isSubmitting.current) return;
    if (!form.title || !form.price || !form.category) {
      showAlert("Missing required fields", "error");
      return;
    }

    if (!form.phone || !form.phone.trim()) {
      showAlert("Phone number is required so buyers can contact you", "error");
      return;
    }

    const phoneRegex = /^(\+251|251|0)[1-9][0-9]{8}$/;
    const cleanedPhone = form.phone.replace(/\s/g, "");
    if (!phoneRegex.test(cleanedPhone)) {
      showAlert("Please enter a valid phone number (e.g. 0912345678 or +251912345678)", "error");
      return;
    }

    // Validate visible required fields
    const visibleFields = fields.filter(shouldShowField);
    for (const field of visibleFields) {
      if (
        field.is_required &&
        (!form.extra[field.name] || !String(form.extra[field.name]).trim())
      ) {
        showAlert(`${field.name} is required`, "error");
        return;
      }
    }
    
    isSubmitting.current = true;
    setLoading(true);
    try {
      const fd = new FormData();

      fd.append("title", form.title);
      fd.append("price", form.price);
      fd.append("phone", cleanedPhone);
      fd.append("category", form.category);
      if (form.extra?.Condition || form.extra?.condition) {
        fd.append("condition", form.extra.Condition || form.extra.condition);
      }
      fd.append("description", form.description);
      fd.append("location", form.location);
      // Filter out fields that are hidden by brand rules (e.g. Processor for Apple)
      const filteredExtra = {};
      Object.entries(form.extra || {}).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        if (k === "Brand" || k === "Model" || k === "Condition") {
          filteredExtra[k] = v;
        } else {
          const fieldDef = fields.find((f) => f.name === k);
          if (!fieldDef || shouldShowField(fieldDef)) {
            filteredExtra[k] = v;
          }
        }
      });

      fd.append("extra", JSON.stringify(filteredExtra));

      images.forEach((img, index) => {
        fd.append("images", img.file);
        if (index === 0) fd.append("coverIndex", 0);
      });

      const res = await axios.post(`${apiUrl}/api/posts`, fd, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });

      const data = await res.data;

      if (data.success !== true) {
        showAlert(data.message || data.error || "Failed to post item", "error");
        return;
      }

      // Keep user storage updated with the submitted phone number
      if (storedUser) {
        const updatedUser = { ...storedUser, phone: cleanedPhone };
        if (localStorage.getItem("user")) {
          localStorage.setItem("user", JSON.stringify(updatedUser));
        } else {
          sessionStorage.setItem("user", JSON.stringify(updatedUser));
        }
      }

      showAlert("Item posted successfully!", "success");
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (err) {
      showAlert(err.response?.data?.error || "Failed to post item", "error");
      console.log(err);
    } finally {
      setLoading(false);
      isSubmitting.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="bg-white rounded-2xl shadow p-6 space-y-6">
          <h1 className="text-3xl font-bold">Post Your Item</h1>

          {/* BASIC */}
          <input
            className="w-full p-3 border rounded-xl"
            placeholder="Item title"
            value={form.title}
            onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
          />

          <input
            type="number"
            className="w-full p-3 border rounded-xl"
            placeholder="Price (ETB)"
            value={form.price}
            onChange={(e) => setForm((s) => ({ ...s, price: e.target.value }))}
          />

          {/* PHONE */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1 ml-1">
              Contact Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              className="w-full p-3 border rounded-xl"
              placeholder="e.g. 0912345678 or +251912345678"
              value={form.phone}
              onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
            />
            <p className="text-xs text-gray-500 mt-1 ml-1">
              Required so interested buyers can call you.
            </p>
          </div>

          {/* CATEGORY */}
          <CategorySelect
            categories={categories}
            form={form}
            setForm={setForm}
          />


          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
            {/* BRAND */}
            {brands.length > 0 && (
              <SearchableSelect
                options={brands}
                value={form.extra.Brand || ""}
                placeholder="Select Brand"
                onChange={(brandId) => {
                  setForm((s) => ({
                    ...s,
                    extra: { ...s.extra, Brand: brandId, Model: "" },
                  }));
                }}
              />
            )}

            {/* MODEL */}
            {models.length > 0 && (
              <SearchableSelect
                options={models.map((m) => ({ id: m.name, name: m.name }))}
                value={form.extra.Model || ""}
                placeholder="Select Model"
                onChange={(val) => handleExtraChange("Model", val)}
              />
            )}

            {/* DYNAMIC GRID */}
            {/* FIELDS */}
            {fields.filter(shouldShowField).map((field) =>
              field.type === "select" ? (
                <div key={field.id} className="flex flex-col">
                  {field.is_required && (
                    <label className="text-xs font-semibold text-gray-700 mb-1 ml-1">
                      {field.name} <span className="text-red-500">*</span>
                    </label>
                  )}
                  <SearchableSelect
                    options={field.options.map((opt) => ({ id: opt.value, name: opt.value }))}
                    value={form.extra[field.name] || ""}
                    placeholder={`Select ${field.name}${field.is_required ? " *" : ""}`}
                    onChange={(val) => handleExtraChange(field.name, val)}
                  />
                </div>
              ) : (
                <div key={field.id} className="flex flex-col">
                  {field.is_required && (
                    <label className="text-xs font-semibold text-gray-700 mb-1 ml-1">
                      {field.name} <span className="text-red-500">*</span>
                    </label>
                  )}
                  <input
                    className="p-3 border rounded-xl"
                    placeholder={`${field.name}${field.is_required ? " *" : ""}`}
                    value={form.extra[field.name] || ""}
                    onChange={(e) =>
                      handleExtraChange(field.name, e.target.value)
                    }
                  />
                </div>
              ),
            )}
          </div>

          {/* DESCRIPTION */}
          <textarea
            rows="4"
            className="w-full p-3 border rounded-xl"
            placeholder="Description"
            onChange={(e) =>
              setForm((s) => ({ ...s, description: e.target.value }))
            }
          />

          {/* IMAGES */}
          <input
            type="file"
            multiple
            hidden
            id="img"
            onChange={handleImageUpload}
          />
          <button
            onClick={() => document.getElementById("img").click()}
            className="w-full p-4 border rounded-xl bg-gray-100"
          >
            Upload Images
          </button>
          <p className="text-sm text-gray-500">
            Max {MAX_IMAGES} images • {MAX_IMAGE_SIZE_MB}MB per image
          </p>

          <div className="grid grid-cols-3 gap-3">
            {images.map((img, i) => (
              <div
                key={i}
                className="relative rounded-xl overflow-hidden group"
              >
                <img
                  src={img.preview}
                  alt=""
                  className="h-full w-full object-contain"
                />

                {/* Delete */}
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 bg-red-600/80 text-white text-xs px-2 py-1 rounded"
                >
                  ✕
                </button>

                {/* Cover */}
                {i !== 0 && (
                  <button
                    onClick={() => setAsCover(i)}
                    className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-1 rounded"
                  >
                    Set cover
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            className={`w-full py-3 rounded-xl text-white transition font-semibold ${
              loading ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 shadow-lg"
            }`}
            onClick={handlePostItem}
            disabled={loading}
          >
            {loading ? "Posting..." : "Post Item"}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
